/**
 * Deterministic content-hash machinery for FMV config files — RUF-441.
 *
 * Extracted verbatim from telemetry-multiplier.config.ts (RUF-447) so both the
 * telemetry config and the generalized formula config (formula.config.ts) share
 * one implementation. The functions are byte-for-byte identical to the original
 * so `defaultTelemetryMultiplierConfig.version` — already persisted in
 * fmv_pricing_snapshot.telemetry_config_version — does not change.
 *
 * A config `version` is a deterministic FNV-1a fingerprint of every field
 * except `version` itself, so editing any value (no code change) yields a
 * different `version`. This is a config-identity fingerprint, NOT a security
 * control.
 */

/**
 * Stable JSON stringify — sorts object keys recursively so that field
 * reordering never changes the hash, only value changes do.
 */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const keys = Object.keys(value as Record<string, unknown>).sort();
  const entries = keys.map(
    (k) => `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`,
  );
  return `{${entries.join(',')}}`;
}

/**
 * FNV-1a 32-bit hash, hex-encoded. Not cryptographic — dependency-free and
 * safe to bundle for both Node and browser targets (no node:crypto / WebCrypto).
 */
export function fnv1a(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Deterministic content hash for a config object, excluding any `version` field. */
export function hashConfig<T extends object>(config: Omit<T, 'version'>): string {
  return fnv1a(stableStringify(config));
}
